/* eslint-disable camelcase, @typescript-eslint/naming-convention */
import React from 'react';
import { ChatMessageList } from './ChatMessageList';
import { mockMessages, mockMessagesWithTallTable } from '../../../../__fixtures__/conversations';
const meta = {
    title: 'Features/AIChatWidget/ChatMessageList',
    component: ChatMessageList,
    decorators: [
        (Story) => (React.createElement("div", { style: { height: 400, width: 500 } },
            React.createElement(Story, null)))
    ]
};
export default meta;
export const Default = {
    args: {
        messages: mockMessages,
        isStreaming: false
    }
};
export const Streaming = {
    args: {
        messages: mockMessages,
        isStreaming: true
    }
};
export const Empty = {
    args: {
        messages: [],
        isStreaming: false
    }
};
// Reproduces IP-14201: scrollIntoView on a tall table response over-scrolls the outer
// container, pushing messages out of view and leaving a blank area.
export const WithTallTable = {
    args: {
        messages: mockMessagesWithTallTable,
        isStreaming: false
    },
    decorators: [
        (Story) => (React.createElement("div", { "data-testid": "outer-scroller", style: { height: 500, width: 500, overflowY: 'auto', background: '#f0f0f0' } },
            React.createElement("div", { style: { height: 500, overflow: 'hidden', display: 'flex', flexDirection: 'column' } },
                React.createElement(Story, null))))
    ]
};
// Human live-chat continuation: one thread, three authors. A hidden Zendesk notice sits in the data
// and must NOT render; the customer's own messages carry their delivery state.
const supportThread = [
    {
        id: 's-u1',
        role: 'user',
        content: 'I need to talk to someone about my recurring series.',
        created_at: '2026-10-27T14:00:00Z'
    },
    {
        id: 's-u2',
        role: 'user',
        content: 'It is on the Front St building.',
        metadata: { origin: 'human_support', delivery: 'sent' },
        created_at: '2026-10-27T14:01:00Z'
    },
    {
        id: 's-h1',
        role: 'human_support',
        content: "Hi, I'm Alex. Let me take a look at series 44.",
        metadata: { origin: 'human_support', author: { kind: 'agent', name: 'Alex' } },
        created_at: '2026-10-27T14:02:00Z'
    },
    {
        id: 's-n1',
        role: 'human_support',
        content: 'Waiting on your response… (automation notice, never shown)',
        metadata: { origin: 'human_support', hidden: true },
        created_at: '2026-10-27T14:03:00Z'
    },
    {
        id: 's-h2',
        role: 'human_support',
        content: 'Here is a walkthrough for that setting.',
        metadata: { origin: 'human_support', author: { kind: 'agent', name: 'Product & Engineering Support' } },
        file_attachments: [
            {
                file_id: 'f1',
                file_name: 'series-settings.png',
                mime_type: 'image/png',
                file_size_bytes: 2048,
                scan_status: 'clean',
                is_image: true
            },
            {
                file_id: 'f2',
                file_name: 'recurring-series-guide.pdf',
                mime_type: 'application/pdf',
                file_size_bytes: 348160,
                scan_status: 'clean'
            }
        ],
        created_at: '2026-10-27T14:04:00Z'
    },
    {
        id: 's-u3',
        role: 'user',
        content: 'Here is what I see on my side.',
        metadata: { origin: 'human_support', delivery: 'sent' },
        file_attachments: [
            {
                file_id: 'c1',
                file_name: 'my-screen.png',
                mime_type: 'image/png',
                file_size_bytes: 91000,
                scan_status: 'clean'
            },
            { file_id: 'c2', file_name: 'visits.csv', mime_type: 'text/csv', file_size_bytes: 4200, scan_status: 'clean' }
        ],
        created_at: '2026-10-27T14:04:30Z'
    },
    {
        id: 'support-pending-1',
        role: 'user',
        content: 'Thanks, trying it now.',
        metadata: { origin: 'human_support', delivery: 'sending' },
        created_at: '2026-10-27T14:05:00Z'
    },
    {
        id: 'support-pending-2',
        role: 'user',
        content: 'Did that go through?',
        metadata: { origin: 'human_support', delivery: 'failed' },
        created_at: '2026-10-27T14:06:00Z'
    }
];
export const HumanSupportConversation = {
    args: {
        messages: supportThread,
        isStreaming: false,
        onRetrySupportMessage: () => undefined,
        // Thumbnails both sides: tap any image to open the lightbox (it has a Download action).
        resolveSupportAttachmentUrl: () => Promise.resolve({
            url: 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="1200" height="800"><rect width="1200" height="800" fill="%23dfe3ea"/><text x="50%25" y="50%25" text-anchor="middle" fill="%236b7280" font-size="48" font-family="sans-serif">photo</text></svg>'
        })
    }
};
// What the customer did with support, kept in the thread so a reload still shows it: small centred
// muted lines (a record, never a bubble, with no action bar).
const supportNotices = [
    {
        id: 'n-u1',
        role: 'user',
        content: 'My recurring series stopped generating visits.',
        created_at: '2026-10-27T14:00:00Z'
    },
    {
        id: 'n-1',
        role: 'system',
        content: 'You started a chat with InspectPoint Support.',
        support_notice: 'chat_started',
        created_at: '2026-10-27T14:01:00Z'
    },
    {
        id: 'n-h1',
        role: 'human_support',
        content: "Hi, I'm Alex. Let me take a look.",
        metadata: { origin: 'human_support', author: { kind: 'agent', name: 'Alex' } },
        created_at: '2026-10-27T14:02:00Z'
    },
    {
        id: 'n-2',
        role: 'system',
        content: 'Chat with InspectPoint Support ended.',
        support_notice: 'chat_ended',
        created_at: '2026-10-27T14:10:00Z'
    },
    {
        id: 'n-3',
        role: 'system',
        content: "You emailed InspectPoint Support. We'll reply to p***@example.com.",
        support_notice: 'email_sent',
        created_at: '2026-10-27T14:20:00Z'
    }
];
export const SupportNotices = {
    args: { messages: supportNotices, isStreaming: false }
};
